/**
 * Textes de l'accueil, en français (maquette validée par Guillaume le
 * 08/10/2026, reprise telle quelle) et en anglais. Les nombres ne sont jamais
 * écrits ici : ils arrivent calculés (chiffres-clés, catalogue, cas clients).
 *
 * Vocabulaire (règles du site) : « labellisé », « crédits carbone
 * attendus », « compensation » permis ; jamais « neutre », « neutralité »,
 * « zéro carbone » ; aucun prix ; aucun nom de porteur ni lieu-dit.
 */
import type { Locale } from '@/lib/site/accueil'

const n = (v: number, locale: Locale) => new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR').format(v)

export const textesAccueil = (locale: Locale) => (locale === 'en' ? EN : FR)
export type TextesAccueil = typeof FR

const FR = {
  meta: {
    titre: 'Stock CO2 — Projets carbone français labellisés, près de vos sites',
    description:
      'Forêts, haies, exploitations agricoles : Stock CO2 choisit avec vous des projets français Label Bas-Carbone, si possible près de vos sites, et vous les fait suivre pas à pas.',
  },
  accroche: {
    surtitre: 'Contribution carbone · Projets français labellisés',
    titre: 'Des projets carbone français que vous voyez grandir.',
    texte:
      'Forêts, haies, exploitations agricoles : nous choisissons avec vous des projets Label bas-carbone, si possible près de vos sites. Vous suivez chaque étape, photos à l’appui, et vous valorisez votre engagement auprès de vos équipes et de vos clients.',
    trouver: 'Trouver mon projet',
    voir: (total: number) => `Voir les ${n(total, 'fr')} projets`,
    // Trois preuves distinctes, discrètes (consigne de Guillaume) ; la fiducie date de 2022, B Corp de 2024
    preuves: [
      'Label bas-carbone',
      'Certifiée B Corp',
      'Fonds en fiducie depuis 2022',
    ],
    photoAlt: 'Forêt française en lumière du matin',
  },
  profils: {
    titre: 'Vous êtes…',
    cartes: [
      {
        cle: 'entreprise',
        titre: 'Une entreprise',
        texte: 'Financez des projets près de vos sites, recevez vos certificats et embarquez vos équipes.',
        lien: 'Trouver mon projet',
        alt: 'Équipe en réunion',
      },
      {
        cle: 'forestier',
        titre: 'Propriétaire forestier',
        texte: 'Faites financer le boisement ou le reboisement de vos parcelles, du montage au suivi.',
        lien: 'Proposer un projet',
        alt: 'Jeune plant forestier',
      },
      {
        cle: 'agriculteur',
        titre: 'Agriculteur',
        texte: 'Valorisez vos pratiques bas-carbone et faites financer la transition de votre exploitation.',
        lien: 'Proposer un projet',
        alt: 'Grandes cultures au printemps',
      },
    ],
  },
  bandeau: 'Ils nous font confiance',
  chiffres: {
    projets: 'projets labellisés',
    hectares: 'de forêts et de terres agricoles',
    contributeurs: 'entreprises et organisations',
    regions: 'régions françaises',
  },
  carte: {
    surtitre: 'Nos projets',
    titre: 'Des projets partout en France, près de chez vous.',
    texte: 'Boisement, reboisement, élevage, grandes cultures, vergers, haies : chaque point est un projet labellisé, avec sa fiche détaillée.',
    tous: 'Tous',
    filtres: 'Filtrer les projets de la carte',
    alt: 'Carte de France des projets labellisés',
    aide: 'Touchez ou cliquez un point pour ouvrir sa fiche.',
    alaUne: 'À la une',
    tco2: (t: number) => `${n(t, 'fr')} tCO₂ attendues`,
    explorer: (total: number) => `Explorer les ${n(total, 'fr')} projets`,
  },
  etapes: {
    titre: 'Comment ça marche',
    liste: [
      { titre: 'On choisit vos projets ensemble', texte: 'Forêt, agriculture ou les deux, près de vos sites ou dans vos régions : vous voyez chaque projet en détail avant de décider.' },
      { titre: 'Vous financez, en toute sécurité', texte: 'Une convention simple, des fonds placés en fiducie et versés aux porteurs de projet au fil des étapes.' },
      { titre: 'Vous suivez et vous en parlez', texte: 'Photos du terrain, certificats, documents et outils de communication, réunis dans votre espace.' },
    ],
  },
  engagements: {
    titre: 'Pourquoi Stock CO2',
    liste: [
      { icone: 'label', titre: 'Un label d’État', texte: 'Tous nos projets suivent le Label bas-carbone du ministère de la Transition écologique, avec des méthodes officielles et un registre public.' },
      { icone: 'sprout', titre: 'Tous types de projets', texte: 'Boisement, reboisement, élevage, grandes cultures, vergers, haies, bâtiment : de quoi composer un portefeuille qui vous ressemble.' },
      { icone: 'search', titre: 'Tout est visible', texte: 'Localisation, surface, essences ou pratiques, carbone attendu, co-bénéfices : chaque fiche projet est ouverte, avant comme après votre contribution.' },
      { icone: 'lock', titre: 'Des contributions sécurisées', texte: 'Fonds placés en fiducie depuis 2022, versés aux porteurs de projet au fil des étapes, et certificats vérifiables en ligne.' },
    ],
  },
  espace: {
    surtitre: 'Espace contributeur',
    titre: 'Tout votre impact. Un seul espace.',
    texte: 'Suivez vos projets, retrouvez vos certificats, embarquez vos équipes. Et centralisez aussi les projets carbone financés ailleurs.',
    decouvrir: 'Découvrir l’espace',
    demo: 'Demander une démo',
    alt: 'Tableau de bord de l’espace contributeur (compte de démonstration)',
  },
  questionnaire: {
    surtitre: '4 questions · 1 minute',
    titre: 'Quel projet pour votre entreprise ?',
    texte: 'Répondez à quatre questions : nous vous montrons trois projets qui vous correspondent, et votre demande de devis est prête.',
    etape: (i: number) => `Question ${i} sur 4`,
    q1: 'Qu’attendez-vous d’abord ?',
    q2: 'Quel volume annuel envisagez-vous ?',
    q2aide: 'Une estimation suffit, ou la taille de votre entreprise.',
    q3: 'Forêt, agriculture ou les deux ?',
    q4: 'Où sont vos sites, et pour quand ?',
    q4regions: 'Régions',
    q4partout: 'Partout en France',
    q4cp: 'Ou codes postaux de vos sites',
    q4cpAide: 'Un ou plusieurs, séparés par une virgule.',
    q4echeance: 'Échéance',
    voirProjets: 'Voir mes trois projets',
    retour: 'Retour',
    recommencer: 'Modifier mes réponses',
    chargement: 'Nous cherchons vos projets…',
    erreurProjets: 'Les projets n’ont pas pu être chargés. Réessayez, ou écrivez-nous à contact@stock-co2.fr.',
    resultatTitre: 'Trois projets qui vous correspondent',
    resultatTexte: 'Des projets réels de notre catalogue, choisis d’après vos réponses. Nous affinerons la sélection avec vous.',
    aucun: 'Aucun projet du catalogue ne correspond exactement : nous vous proposerons une sélection sur mesure.',
    distance: (km: number) => (km < 10 ? 'Tout près de vos sites' : `À ${n(km, 'fr')} km de vos sites`),
    dansRegion: 'Dans votre région',
    voirFiche: 'Voir la fiche',
    formTitre: 'Recevez votre proposition',
    formTexte: 'Vos réponses et ces trois projets sont joints à votre demande. Un interlocuteur unique vous recontacte.',
    nom: 'Nom et prénom',
    email: 'E-mail professionnel',
    entreprise: 'Entreprise',
    telephone: 'Téléphone',
    facultatif: 'facultatif',
    consentement: 'J’accepte que mes données soient traitées par Stock CO2 pour répondre à ma demande, conformément à la',
    confidentialite: 'politique de confidentialité',
    envoyer: 'Envoyer ma demande',
    envoi: 'Envoi…',
    merciTitre: 'Merci, votre demande est enregistrée.',
    merciTexte: 'Nous revenons vers vous rapidement avec une proposition construite sur ces projets.',
    erreurEnvoi: 'Votre demande n’a pas pu être enregistrée. Écrivez-nous à contact@stock-co2.fr, nous reprendrons contact rapidement.',
    progression: 'Progression du questionnaire',
  },
  cas: {
    titre: 'Ils financent des projets avec nous',
    tous: 'Tous les cas clients',
    secteurs: { groupama: 'Assurance', icade: 'Immobilier', dalkia: 'Énergie', sim: 'Industrie' } as Record<string, string>,
    familles: (foret: boolean, agri: boolean): string => (foret && agri ? 'Forêt et agriculture' : foret ? 'Projets forestiers' : 'Projets agricoles'),
    depuis: (a: number) => `depuis ${a}`,
  },
  temoignages: { surtitre: 'Témoignages', titre: 'Ils en parlent' },
  impact: { texte: 'Au-delà du carbone : biodiversité, eau, sols et emploi local.', lien: 'Notre impact' },
  final: {
    titre: 'Parlons de votre projet.',
    texte: 'Un interlocuteur unique, du choix des projets au certificat.',
    trouver: 'Trouver mon projet',
    contact: 'Nous contacter',
  },
}

const EN: TextesAccueil = {
  meta: {
    titre: 'Stock CO2 — Labelled French carbon projects, close to your sites',
    description:
      'Forests, hedges, farms: Stock CO2 selects with you French projects labelled under the Label Bas-Carbone, close to your sites where possible, and lets you follow them step by step.',
  },
  accroche: {
    surtitre: 'Carbon contribution · Labelled French projects',
    titre: 'French carbon projects you can watch grow.',
    texte:
      'Forests, hedges, farms: we select with you projects labelled under the Label Bas-Carbone, close to your sites where possible. You follow every stage, with photos, and you share your commitment with your teams and your customers.',
    trouver: 'Find my project',
    voir: (total: number) => `See the ${n(total, 'en')} projects`,
    preuves: [
      'Label Bas-Carbone',
      'Certified B Corp',
      'Funds held in trust since 2022',
    ],
    photoAlt: 'French forest in the morning light',
  },
  profils: {
    titre: 'You are…',
    cartes: [
      { cle: 'entreprise', titre: 'A company', texte: 'Fund projects close to your sites, receive your certificates and get your teams on board.', lien: 'Find my project', alt: 'Team meeting' },
      { cle: 'forestier', titre: 'A forest owner', texte: 'Get the afforestation or reforestation of your plots funded, from set-up to monitoring.', lien: 'Submit a project', alt: 'Young forest seedling' },
      { cle: 'agriculteur', titre: 'A farmer', texte: 'Make the most of your low-carbon practices and get your farm’s transition funded.', lien: 'Submit a project', alt: 'Field crops in spring' },
    ],
  },
  bandeau: 'They trust us',
  chiffres: {
    projets: 'labelled projects',
    hectares: 'of forests and farmland',
    contributeurs: 'companies and organisations',
    regions: 'French regions',
  },
  carte: {
    surtitre: 'Our projects',
    titre: 'Projects all over France, close to you.',
    texte: 'Afforestation, reforestation, livestock, field crops, orchards, hedges: each dot is a labelled project, with its detailed page.',
    tous: 'All',
    filtres: 'Filter the projects on the map',
    alt: 'Map of France showing the labelled projects',
    aide: 'Tap or click a dot to open its page.',
    alaUne: 'Featured',
    tco2: (t: number) => `${n(t, 'en')} tCO₂ expected`,
    explorer: (total: number) => `Explore the ${n(total, 'en')} projects`,
  },
  etapes: {
    titre: 'How it works',
    liste: [
      { titre: 'We choose your projects together', texte: 'Forest, farming or both, close to your sites or in your regions: you see every project in detail before deciding.' },
      { titre: 'You fund, securely', texte: 'A simple agreement, funds held in trust and paid to project owners stage by stage.' },
      { titre: 'You follow and you share', texte: 'Field photos, certificates, documents and communication tools, all in your space.' },
    ],
  },
  engagements: {
    titre: 'Why Stock CO2',
    liste: [
      { icone: 'label', titre: 'A state label', texte: 'All our projects follow the Label Bas-Carbone of the French Ministry for Ecological Transition, with official methods and a public register.' },
      { icone: 'sprout', titre: 'Every type of project', texte: 'Afforestation, reforestation, livestock, field crops, orchards, hedges, buildings: enough to build a portfolio that suits you.' },
      { icone: 'search', titre: 'Everything is visible', texte: 'Location, area, species or practices, expected carbon, co-benefits: every project page is open, before and after your contribution.' },
      { icone: 'lock', titre: 'Secure contributions', texte: 'Funds held in trust since 2022, paid to project owners stage by stage, and certificates you can verify online.' },
    ],
  },
  espace: {
    surtitre: 'Contributor space',
    titre: 'All your impact. One space.',
    texte: 'Follow your projects, find your certificates, get your teams on board. And bring together carbon projects funded elsewhere too.',
    decouvrir: 'Discover the space',
    demo: 'Request a demo',
    alt: 'Contributor space dashboard (demo account)',
  },
  questionnaire: {
    surtitre: '4 questions · 1 minute',
    titre: 'Which project for your company?',
    texte: 'Answer four questions: we show you three projects that suit you, and your quote request is ready.',
    etape: (i: number) => `Question ${i} of 4`,
    q1: 'What do you expect first?',
    q2: 'What annual volume do you have in mind?',
    q2aide: 'An estimate is enough, or the size of your company.',
    q3: 'Forest, farming or both?',
    q4: 'Where are your sites, and when?',
    q4regions: 'Regions',
    q4partout: 'Anywhere in France',
    q4cp: 'Or postcodes of your sites',
    q4cpAide: 'One or more, separated by commas.',
    q4echeance: 'Timing',
    voirProjets: 'See my three projects',
    retour: 'Back',
    recommencer: 'Change my answers',
    chargement: 'Looking for your projects…',
    erreurProjets: 'The projects could not be loaded. Please try again, or write to us at contact@stock-co2.fr.',
    resultatTitre: 'Three projects that suit you',
    resultatTexte: 'Real projects from our catalogue, chosen from your answers. We will refine the selection with you.',
    aucun: 'No project in the catalogue matches exactly: we will suggest a tailored selection.',
    distance: (km: number) => (km < 10 ? 'Right next to your sites' : `${n(km, 'en')} km from your sites`),
    dansRegion: 'In your region',
    voirFiche: 'See the project',
    formTitre: 'Receive your proposal',
    formTexte: 'Your answers and these three projects are attached to your request. A single contact person will get back to you.',
    nom: 'Full name',
    email: 'Work email',
    entreprise: 'Company',
    telephone: 'Phone',
    facultatif: 'optional',
    consentement: 'I agree that my data is processed by Stock CO2 to answer my request, in accordance with the',
    confidentialite: 'privacy policy',
    envoyer: 'Send my request',
    envoi: 'Sending…',
    merciTitre: 'Thank you, your request has been recorded.',
    merciTexte: 'We will get back to you shortly with a proposal built on these projects.',
    erreurEnvoi: 'Your request could not be recorded. Please write to us at contact@stock-co2.fr and we will get back to you quickly.',
    progression: 'Questionnaire progress',
  },
  cas: {
    titre: 'They fund projects with us',
    tous: 'All case studies',
    secteurs: { groupama: 'Insurance', icade: 'Real estate', dalkia: 'Energy', sim: 'Manufacturing' },
    familles: (foret: boolean, agri: boolean) => (foret && agri ? 'Forest and farming' : foret ? 'Forest projects' : 'Farming projects'),
    depuis: (a: number) => `since ${a}`,
  },
  temoignages: { surtitre: 'Testimonials', titre: 'What they say' },
  impact: { texte: 'Beyond carbon: biodiversity, water, soils and local employment.', lien: 'Our impact' },
  final: {
    titre: 'Let’s talk about your project.',
    texte: 'A single contact person, from choosing the projects to the certificate.',
    trouver: 'Find my project',
    contact: 'Contact us',
  },
}
